import { lazy, Suspense, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Route } from '@src/routes/public-notices';
import { PublicNoticeQueries, PN_QUERY_KEYS } from '@api/services/public-notices';
import { usePublicNotices, useTableStore } from '@stores';

import PublicNoticesTable from './public-notices-table/PublicNoticesTable';
import PublicNoticesFilters from './PublicNoticesFilters';

import classnames from 'classnames';
import './publicNotices.scss';

import { Button, ButtonGroup } from '@trussworks/react-uswds';
import Loader from '@components/loader/Loader';
import PublicNoticeFilterTags from './PublicNoticeFilterTags';
import { parseSearchList } from './publicNoticeHelpers';
import { publicNoticesTableId } from '@pages/PublicNotices/public-notices-table/PublicNoticesTable';

const PublicNoticesMap = lazy(() => import('./public-notices-map/PublicNoticesMap'));

const PublicNotices = () => {
  const { district, state, page, search } = Route.useSearch();
  const hasAppliedUrlSearch = useRef(false);

  const { getPageNumber, getPageSize, getRecordCount, getSortColumn, getSortDirection, setPageNumber, setRecordCount } =
    useTableStore();

  const itemsPerPage = getPageSize(publicNoticesTableId);
  const pageNumber = getPageNumber(publicNoticesTableId);
  const recordCount = getRecordCount(publicNoticesTableId);
  const sortColumn = getSortColumn(publicNoticesTableId);
  const sortOrder = getSortDirection(publicNoticesTableId);

  const {
    debouncedSearchText,
    districtCodes,
    setDistrictCodes,
    setSearchText,
    setStateCodes,
    setView,
    stateCodes,
    view,
  } = usePublicNotices();

  const queryCount = view === 'table' ? itemsPerPage : recordCount;

  const { data, isLoading } = useQuery({
    queryKey: [
      PN_QUERY_KEYS.GET_ALL,
      districtCodes,
      itemsPerPage,
      pageNumber,
      debouncedSearchText,
      sortColumn,
      sortOrder,
      stateCodes,
      queryCount,
    ],
    queryFn: () =>
      PublicNoticeQueries.getAll({
        districtCodes,
        itemsPerPage: queryCount,
        pageNumber,
        searchText: debouncedSearchText,
        sortColumn,
        sortOrder,
        states: stateCodes,
      }),
  });

  const tableStyles = classnames({
    'display-none': view !== 'table',
  });
  const mapStyles = classnames({
    'display-none': view !== 'map',
  });

  useEffect(() => {
    if (!data?.totalFilteredCount) return;

    setRecordCount(publicNoticesTableId, data.totalFilteredCount);
  }, [setRecordCount, data?.totalFilteredCount]);

  useEffect(() => {
    if (hasAppliedUrlSearch.current) return;

    const districtList = parseSearchList(district);
    const stateList = parseSearchList(state);
    districtList.length && setDistrictCodes(districtList);
    stateList.length && setStateCodes(stateList);
    search && setSearchText(search);
    page && setPageNumber(page);
    hasAppliedUrlSearch.current = true;
  }, [district, state, search, page, setDistrictCodes, setStateCodes, setSearchText, setPageNumber]);

  return (
    <div className='content-container'>
      <h1>Public Notices</h1>
      <div className='table-content'>
        <PublicNoticesFilters />
        <div className='pn-records'>
          <div className='pn-results-count'>Results Count: {recordCount}</div>
          <Loader showLoader={isLoading} variant='page' />
          {isLoading ? null : (
            <>
              <ButtonGroup className='pn-view-toggle' type='segmented'>
                <Button
                  aria-selected={view === 'table'}
                  className='page-control'
                  onClick={() => setView('table')}
                  outline={view !== 'table'}
                >
                  Table view
                </Button>
                <Button
                  aria-selected={view === 'map'}
                  className='page-control'
                  onClick={() => setView('map')}
                  outline={view !== 'map'}
                >
                  Map view
                </Button>
              </ButtonGroup>
              <PublicNoticeFilterTags />
              <div className={tableStyles}>
                <PublicNoticesTable publicNotices={data?.publicNotices} />
              </div>
              <div className={mapStyles}>
                {view === 'map' && (
                  <Suspense fallback={<Loader variant='section' />}>
                    <PublicNoticesMap publicNotices={data?.publicNotices} />
                  </Suspense>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
export default PublicNotices;
